// hooks/useCrm.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { 
  Contact, 
  ContactListItem, 
  ContactFormData, 
  ContactSource,
  ContactNote,
  BulkOperationData,
  ContactFilters,
  HotLead,
  StaleLead,
  User,
  LeadScore,
  PaginatedResponse
} from '../types/crm';


// Contact hooks
export const useContacts = (filters?: ContactFilters) => {
  return useQuery({
    queryKey: ['contacts', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      if (filters?.search) params.append('search', filters.search);
      if (filters?.contact_type?.length) {
        filters.contact_type.forEach(type => params.append('contact_type', type));
      }
      if (filters?.qualification_status?.length) {
        filters.qualification_status.forEach(status => params.append('qualification_status', status));
      }
      if (filters?.assigned_to?.length) {
        filters.assigned_to.forEach(id => params.append('assigned_to', id.toString()));
      }
      if (filters?.source_id?.length) {
        filters.source_id.forEach(id => params.append('source', id.toString()));
      }
      if (filters?.lead_score_min !== undefined) {
        params.append('lead_score__gte', filters.lead_score_min.toString());
      }
      if (filters?.lead_score_max !== undefined) {
        params.append('lead_score__lte', filters.lead_score_max.toString());
      }
      if (filters?.tags?.length) {
        filters.tags.forEach(id => params.append('tags', id.toString()));
      }
      if (filters?.ordering) params.append('ordering', filters.ordering);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.page_size) params.append('page_size', filters.page_size.toString());

      const response = await apiClient.get<ContactListItem[] | PaginatedResponse<ContactListItem>>(`/api/crm/contacts/?${params.toString()}`);
      const data = response.data;
      if (Array.isArray(data)) {
        return { count: data.length, results: data, next: null, previous: null } as PaginatedResponse<ContactListItem>;
      }
      return data;
    },
  });
};

export const useContact = (id: number) => {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: async () => {
      const response = await apiClient.get<Contact>(`/api/crm/contacts/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export interface ContactNeighbors {
  position: number | null;
  total: number;
  prev_id: number | null;
  next_id: number | null;
}

export const useContactNeighbors = (id: number) => {
  return useQuery({
    queryKey: ['contact-neighbors', id],
    queryFn: async () => {
      const response = await apiClient.get<ContactNeighbors>(`/api/crm/contacts/${id}/neighbors/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useContactSources = () => {
  return useQuery({
    queryKey: ['contactSources'],
    queryFn: async () => {
      const response = await apiClient.get<ContactSource[] | PaginatedResponse<ContactSource>>('/api/crm/contact-sources/');
      // Handle paginated response structure
      const data = response.data;
      return Array.isArray(data) ? data : data.results;
    },
  });
};

export const useCreateContactSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const response = await apiClient.createContactSource(name);
      return response.data;
    },
    onSuccess: (newSource) => {
      queryClient.setQueryData<ContactSource[]>(['contactSources'], (old) =>
        old ? [...old, newSource] : [newSource]
      );
    },
  });
};

// Contact mutations
export const useCreateContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (contactData: ContactFormData) => {
      const response = await apiClient.post<Contact>('/api/crm/contacts/', contactData);
      return response.data;
    },
    onSuccess: (newContact) => {
      // Set the new contact in the cache immediately
      queryClient.setQueryData(['contact', newContact.id], newContact);
      // Also invalidate the contacts list to refresh it
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

export const useUpdateContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<ContactFormData> }) => {
      const response = await apiClient.patch<Contact>(`/api/crm/contacts/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', id] });
    },
  });
};

export const useDeleteContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/crm/contacts/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

export const useImportContacts = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (formData: FormData) => {
      // apiClient automatically handles FormData and sets the correct headers
      const response = await apiClient.post<{
        message: string;
        success_count: number;
        error_count: number;
        errors: string[];
      }>('/api/crm/contacts/import_contacts/', formData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

// Users hook for assignment dropdown
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await apiClient.get<User[] | { results: User[] }>('/api/crm/users/');
      // Handle both paginated and non-paginated responses
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        return response.data.results || [];
      }
    },
  });
};

// Contact assignment update hook
export const useUpdateContactAssignment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ contactId, assignedTo }: { contactId: number; assignedTo: number | null }) => {
      const response = await apiClient.patch<Contact>(`/api/crm/contacts/${contactId}/`, {
        assigned_to: assignedTo,
      });
      return response.data;
    },
    onSuccess: (updatedContact, { contactId, assignedTo }) => {
      // Use updater function to merge only the assignment fields without overwriting other changes
      queryClient.setQueryData(['contact', contactId], (oldContact: Contact | undefined) => {
        if (!oldContact) return updatedContact;
        
        return {
          ...oldContact,
          assigned_to: assignedTo,
          assigned_date: updatedContact.assigned_date,
        };
      });
      
      // Only invalidate the contacts list, not the individual contact
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

// Contact qualification status update hook
export const useUpdateQualificationStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ contactId, qualificationStatus }: { contactId: number; qualificationStatus: string }) => {
      const response = await apiClient.patch<Contact>(`/api/crm/contacts/${contactId}/`, {
        qualification_status: qualificationStatus,
      });
      return response.data;
    },
    onSuccess: (updatedContact, { contactId, qualificationStatus }) => {
      // Use updater function to merge only the qualification status without overwriting other changes
      queryClient.setQueryData(['contact', contactId], (oldContact: Contact | undefined) => {
        if (!oldContact) return updatedContact;
        
        return {
          ...oldContact,
          qualification_status: qualificationStatus,
        };
      });
      
      // Invalidate the contacts list to refresh any filtering/display
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

// Lead scoring hooks
export const useContactScoring = (contactId: number) => {
  return useQuery<LeadScore>({
    queryKey: ['contactScoring', contactId],
    queryFn: async () => {
      const response = await apiClient.get<LeadScore>(`/api/crm/contacts/${contactId}/scoring_breakdown/`);
      return response.data;
    },
    enabled: !!contactId,
  });
};

export const useRecalculateScore = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ contactId, adjustment = 0, reason = 'Manual recalculation' }: {
      contactId: number;
      adjustment?: number;
      reason?: string;
    }) => {
      const response = await apiClient.post(`/api/crm/contacts/${contactId}/recalculate_score/`, {
        manual_adjustment: adjustment,
        adjustment_reason: reason,
      });
      return response.data;
    },
    onSuccess: (_, { contactId }) => {
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
      queryClient.invalidateQueries({ queryKey: ['contactScoring', contactId] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

// Lead assignment hooks
export const useBulkAssignContacts = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: BulkOperationData) => {
      const response = await apiClient.post('/api/crm/distribution-settings/bulk_assign/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

export const useAssignContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      contactId, 
      assignedTo, 
      reason = 'Manual assignment' 
    }: {
      contactId: number;
      assignedTo?: number;
      reason?: string;
    }) => {
      const response = await apiClient.post('/api/crm/distribution-settings/assign_single/', {
        contact_id: contactId,
        assigned_to: assignedTo,
        assignment_reason: reason,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

// Hot and stale leads
export const useHotLeads = (threshold = 80) => {
  return useQuery<HotLead[]>({
    queryKey: ['hotLeads', threshold],
    queryFn: async () => {
      const response = await apiClient.get<HotLead[]>(`/api/crm/contacts/hot_leads/?threshold=${threshold}`);
      return response.data;
    },
  });
};

export const useStaleLeads = (days = 30) => {
  return useQuery<StaleLead[]>({
    queryKey: ['staleLeads', days],
    queryFn: async () => {
      const response = await apiClient.get<StaleLead[]>(`/api/crm/contacts/stale_leads/?days=${days}`);
      return response.data;
    },
  });
};

// Lead scoring insights
export const useScoringInsights = () => {
  return useQuery({
    queryKey: ['scoringInsights'],
    queryFn: async () => {
      const response = await apiClient.get('/api/crm/contacts/scoring_insights/');
      return response.data;
    },
  });
};

// Contact Notes hooks
export const useContactNotes = (contactId?: number) => {
  return useQuery({
    queryKey: ['contactNotes', contactId],
    queryFn: async () => {
      if (!contactId) return [];
      const response = await apiClient.get<ContactNote[] | PaginatedResponse<ContactNote>>(`/api/crm/contact-notes/?contact=${contactId}&ordering=-created_at`);
      // Handle paginated response structure
      const data = response.data;
      return Array.isArray(data) ? data : data.results;
    },
    enabled: !!contactId,
  });
};

export const useCreateContactNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteData: Omit<ContactNote, 'id' | 'created_by' | 'created_by_name' | 'created_at' | 'updated_at'>) => {
      const response = await apiClient.post<ContactNote>('/api/crm/contact-notes/', noteData);
      return response.data;
    },
    onSuccess: async (newNote) => {
      // Invalidate contact notes queries
      queryClient.invalidateQueries({ queryKey: ['contactNotes', newNote.contact] });
      // Also invalidate the contact detail to refresh the notes count
      queryClient.invalidateQueries({ queryKey: ['contact', newNote.contact] });
      // Force immediate refetch to update UI
      await queryClient.refetchQueries({ queryKey: ['contactNotes', newNote.contact] });
    },
  });
};

export const useUpdateContactNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...noteData }: Partial<ContactNote> & { id: number }) => {
      const response = await apiClient.patch<ContactNote>(`/api/crm/contact-notes/${id}/`, noteData);
      return response.data;
    },
    onSuccess: (updatedNote) => {
      // Invalidate contact notes queries
      queryClient.invalidateQueries({ queryKey: ['contactNotes', updatedNote.contact] });
      // Also invalidate the contact detail
      queryClient.invalidateQueries({ queryKey: ['contact', updatedNote.contact] });
    },
  });
};

export const useDeleteContactNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, contactId }: { id: number; contactId: number }) => {
      await apiClient.delete(`/api/crm/contact-notes/${id}/`);
      return { id, contactId };
    },
    onSuccess: ({ contactId }) => {
      // Invalidate contact notes queries
      queryClient.invalidateQueries({ queryKey: ['contactNotes', contactId] });
      // Also invalidate the contact detail
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
    },
  });
};

// Generic bulk operations
export const useBulkOperation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      contact_ids: number[];
      operation: string;
      [key: string]: any;
    }) => {
      const response = await apiClient.post('/api/crm/contacts/bulk_update/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

// Related jobs for contact
export const useContactRelatedJobs = (contactId: number) => {
  return useQuery({
    queryKey: ['contactJobs', contactId],
    queryFn: async () => {
      const response = await apiClient.get(`/api/crm/contacts/${contactId}/related_jobs/`);
      return response.data;
    },
    enabled: !!contactId,
  });
};